import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { backendUrl, frontendUrl, oauthProvider } from '../config/environment';
import './Dashboard.css';

interface UserInfo {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  [key: string]: any;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');

  useEffect(() => {
    // First, try to get session_id from URL query parameters
    const urlSessionId = searchParams.get('session_id');
    let currentSessionId = '';
    
    if (urlSessionId) {
      currentSessionId = urlSessionId;
      // Store it in localStorage for future use
      localStorage.setItem('oauth_session_id', urlSessionId);
    } else {
      // If not in URL, try to get from localStorage
      const storedSessionId = localStorage.getItem('oauth_session_id');
      if (storedSessionId) {
        currentSessionId = storedSessionId;
      }
    }

    const storedAccessToken = localStorage.getItem('access_token');
    const storedUserInfo = localStorage.getItem('user_info');

    if (!currentSessionId || !storedAccessToken) {
      // Redirect to login if not authenticated
      navigate('/');
      return;
    }

    setSessionId(currentSessionId);
    setAccessToken(storedAccessToken);

    if (storedUserInfo) {
      try {
        setUserInfo(JSON.parse(storedUserInfo));
      } catch (err) {
        console.error('Failed to parse user info:', err);
      }
    }
  }, [navigate, searchParams]);

  const handleLogout = () => {
    // Clear all stored data
    localStorage.removeItem('oauth_session_id');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
    
    // Redirect to login
    navigate('/');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>UTrade MCP Dashboard</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>

        <div className="dashboard-content">
          <div className="info-section">
            <h2>Authentication Status</h2>
            <div className="status-badge success">
              ✅ Successfully Authenticated
            </div>
          </div>

          <div className="info-section">
            <h2>Session Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Session ID:</label>
                <div className="info-value">
                  <span className="session-id">{sessionId}</span>
                  <button 
                    onClick={() => copyToClipboard(sessionId)}
                    className="copy-button"
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </div>
              </div>
              
              <div className="info-item">
                <label>Access Token:</label>
                <div className="info-value">
                  <span className="access-token">
                    {accessToken.substring(0, 20)}...
                  </span>
                  <button 
                    onClick={() => copyToClipboard(accessToken)}
                    className="copy-button"
                    title="Copy full token to clipboard"
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>
          </div>

          {userInfo && (
            <div className="info-section">
              <h2>User Information</h2>
              <div className="info-grid">
                {Object.entries(userInfo).map(([key, value]) => (
                  <div key={key} className="info-item">
                    <label>{key.replace(/_/g, ' ').toUpperCase()}:</label>
                    <span className="info-value">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="info-section">
            <h2>MCP Integration</h2>
            <div className="integration-info">
              <p>
                <strong>Backend URL:</strong> {backendUrl}
              </p>
              <p>
                <strong>Frontend URL:</strong> {frontendUrl}
              </p>
              <p>
                <strong>OAuth Provider:</strong> {oauthProvider}
              </p>
            </div>
          </div>

          <div className="info-section">
            <h2>Usage Instructions</h2>
            <div className="usage-instructions">
              <ol>
                <li>Use the Session ID above in your MCP client configuration</li>
                <li>Include the Access Token in your API requests</li>
                <li>The session remains valid until you logout or it expires</li>
                <li>All trading operations require this authenticated session</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 